<template>
  <div
    :class="['catalog-item', { 'is-active': isActive, 'is-selected': isSelected }]"
    :data-qa="`catalog-item-${accessory.OptionType_ID}`"
  >
    <!--
      The three content columns are the only route to the detail panel, so they
      have to be operable, not just clickable. They carry the role instead of the
      row itself: a `role="button"` prunes its descendants from the accessibility
      tree, which would have swallowed the add/remove button sitting next to them.
      As siblings both stay reachable and neither activation reaches the other.
    -->
    <div
      class="catalog-item__open"
      role="button"
      tabindex="0"
      :data-qa="`catalog-item-open-${accessory.OptionType_ID}`"
      @click="$emit('open', accessory)"
      @keydown.enter.prevent="$emit('open', accessory)"
      @keydown.space.prevent="$emit('open', accessory)"
    >
      <accessory-thumb :src="thumbnailUrl" :alt="title" class="catalog-item__thumb" />
      <div>
        <div class="catalog-item__title">
          {{ title }}
          <span v-if="actionActive" class="catalog-item__tag">{{
            i18n.t('/AccessoryCatalog/TagAction')
          }}</span>
          <span v-if="!enriched" class="catalog-item__tag catalog-item__tag--grey">
            {{ i18n.t('/AccessoryCatalog/TagOwnDealerOption') }}
          </span>
        </div>
        <p v-if="description" class="catalog-item__desc">{{ description }}</p>
        <div class="catalog-item__path">
          <template v-if="path">{{ path }} · </template><code>{{ accessory.ID }}</code>
          <template v-if="stockLabel">
            ·
            <span :class="['catalog-item__stock', { 'catalog-item__stock--no': !inStock }]">
              {{ stockLabel }}
            </span>
            <!--
              The import-of-last-night caveat used to live in a `title` attribute
              on this span, where neither a keyboard nor a touch user could get
              at it. It is a qualification of the number next to it, so it is
              rendered.
            -->
            <span class="catalog-item__stockmeta">{{ i18n.t('/AccessoryCatalog/StockMeta') }}</span>
          </template>
        </div>
      </div>
      <div class="catalog-item__rowprice">
        <div class="catalog-item__price">
          <!--
            A priceless accessory is not a free one. `n()` coerces a non-finite
            value to zero and logs, so an em dash stands in for "no price known".
          -->
          <template v-if="price !== null">{{ i18n.n(price, 'currency') }}</template>
          <template v-else>{{ NO_PRICE }}</template>
        </div>
        <div v-if="struckPrice" class="catalog-item__was">
          {{ i18n.n(struckPrice, 'currency') }}
        </div>
        <div class="catalog-item__sub">{{ vatLabel }}</div>
      </div>
    </div>
    <button
      v-if="isEditable"
      :class="['catalog-item__add', { on: isSelected }]"
      type="button"
      :title="toggleLabel"
      :aria-label="toggleLabel"
      :data-qa="`catalog-item-toggle-${accessory.OptionType_ID}`"
      @click="$emit('toggle', accessory)"
    >
      <!-- Icon.vue renders its svg as role="presentation": its own title never
           reaches assistive tech, so the name lives on the button. -->
      <icon v-if="isSelected" icon="check" aria-hidden="true" />
      <icon v-else icon="plus" aria-hidden="true" />
    </button>
  </div>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import AccessoryThumb from './AccessoryThumb.vue'
import {
  VAT_EXCL,
  VAT_INCL,
  categoryPath,
  hasActiveAction,
  hasLabour,
  isEnriched,
  offeredPrice,
  toNumber,
  wasPrice,
} from '@/helpers/ponCatalog'
import { displayTitle, plainText } from '@/helpers/ponCatalogFilters'

/** Stands in for a price the catalog does not have; never a formatted zero. */
const NO_PRICE = '—'

export default {
  components: {
    Icon,
    AccessoryThumb,
  },
  props: {
    accessory: {
      type: Object,
      required: true,
    },
    /** The matching quote row, when this accessory is already on the quote */
    quoteRow: {
      type: Object,
      default: null,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    /** Which price this line shows: VAT_INCL or VAT_EXCL */
    vat: {
      type: String,
      default: VAT_INCL,
    },
    /**
     * The catalog's shared clock (see mixins/catalogClock). Computeds cache on
     * their reactive dependencies and `new Date()` is not one, so without this
     * an action expiring while the catalog is open leaves the row showing the
     * action price and the "Actie" tag until something else invalidates it.
     */
    now: {
      type: Date,
      default: null,
    },
  },
  emits: ['toggle', 'open'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      NO_PRICE,
    }
  },
  computed: {
    /** The instant every action question is answered against. */
    clock() {
      return this.now || new Date()
    },
    pon() {
      return this.accessory.PonAccessoryCatalog || null
    },
    enriched() {
      return isEnriched(this.accessory)
    },
    title() {
      return displayTitle(this.accessory)
    },
    thumbnailUrl() {
      return this.pon?.ThumbnailUrl || null
    },
    description() {
      return plainText(this.pon?.Description)
    },
    path() {
      return categoryPath(this.accessory).join(' › ')
    },
    /**
     * The leading price. In the catalog that is the price the accessory is on
     * offer at — the action price while an action runs. On the quote it is the
     * row's own price, which is what the customer is actually charged: after an
     * action-price choice or a manual edit that differs from the catalog.
     *
     * Null when neither side has a price, which the template renders as a dash.
     */
    price() {
      if (this.quoteRow) return toNumber(this.quoteRow.Price?.[this.vat])
      return offeredPrice(this.accessory, this.vat, this.clock)
    },
    actionActive() {
      return hasActiveAction(this.accessory, this.clock)
    },
    struckPrice() {
      return wasPrice(this.accessory, this.price, this.vat, this.clock)
    },
    hasMontage() {
      return hasLabour(this.accessory)
    },
    vatLabel() {
      if (this.vat === VAT_EXCL)
        return this.hasMontage
          ? this.i18n.t('/AccessoryCatalog/MountedExclVat')
          : this.i18n.t('/AccessoryCatalog/ExclVat')
      return this.hasMontage
        ? this.i18n.t('/AccessoryCatalog/MountedInclVat')
        : this.i18n.t('/AccessoryCatalog/InclVat')
    },
    toggleLabel() {
      return this.isSelected
        ? this.i18n.t('/AccessoryCatalog/RemoveFromQuote')
        : this.i18n.t('/AccessoryCatalog/AddToQuote')
    },
    inStock() {
      return (this.pon?.Stock ?? 0) > 0
    },
    stockLabel() {
      if (!this.enriched || this.pon.Stock === null) return null
      return this.inStock
        ? this.i18n.t('/AccessoryCatalog/StockAvailable', { count: this.pon.Stock })
        : this.i18n.t('/AccessoryCatalog/StockUnavailable')
    },
  },
}
</script>

<style lang="scss" scoped>
// The row is denser than the body scale ($font-size-base is 18px), so it needs
// steps below it that no global scale defines. Local rather than new global
// tokens: nothing outside this row uses them.
$row-font-tag: 10.5px;
$row-font-code: 11px;
$row-font-meta: 11.5px;
$row-font-was: 12.5px;
$row-font-desc: 13px;
$row-font-title: 15px;
$row-font-price: 16px;

// Fixed columns: the thumb is square, the price column is sized to the widest
// amount plus its VAT suffix and the action column to the round toggle.
$col-thumb: 92px;
$col-price: 176px;
$col-action: 44px;
$col-thumb-sm: 68px;
$col-action-sm: 40px;
$col-gap: $gutter * 1.125;

// Keeps the tag readable on a light brand tint without adding a second colour.
$tag-border: rgba($black, 0.06);

// Foreground on a --color-primary-light background. --color-primary is a brand
// accent meant for fills and borders: on its own light tint it drops to 1.9:1
// (SEAT) and 3.7:1 (CUPRA), both under the 4.5:1 AA floor. $text-color clears
// 9.3:1 on the darkest tint any of the six brands ships.
$on-primary-light: $text-color;

.catalog-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) $col-action;
  gap: $col-gap;
  align-items: center;
  padding: 14px 24px;
  border-bottom: 1px solid $gray-lightest;
  box-shadow: inset 3px 0 0 transparent;
  transition:
    background 0.12s ease,
    box-shadow 0.12s ease;

  &:hover {
    background: $gray-lightest;
    box-shadow: inset 3px 0 0 $gray-light;
  }

  &.is-active {
    background: var(--color-primary-light);
    box-shadow: inset 3px 0 0 var(--color-primary);
  }

  &.is-selected {
    box-shadow: inset 3px 0 0 var(--color-primary);

    .catalog-item__title,
    .catalog-item__price {
      color: var(--color-primary);
    }
  }

  // The active row's background is the brand's light tint; see $on-primary-light
  &.is-active.is-selected {
    .catalog-item__title,
    .catalog-item__price {
      color: $on-primary-light;
    }
  }

  &__open {
    display: grid;
    grid-template-columns: $col-thumb minmax(0, 1fr) $col-price;
    gap: $col-gap;
    align-items: center;
    min-width: 0;
    cursor: pointer;

    // The app sets a global `*:focus { outline: 1px dotted }` in styles/app/_accessibility.scss,
    // which paints on mouse click too. Suppress it here so only keyboard focus is indicated.
    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }
  }

  &__thumb {
    height: 70px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-size: $row-font-title;
    font-weight: 600;
    color: $text-color;
    line-height: 1.25;
  }

  &__tag {
    flex: 0 0 auto;
    font-size: $row-font-tag;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 3px 9px;
    border-radius: 2em;
    background: var(--color-primary-light);
    color: $on-primary-light;
    box-shadow: inset 0 0 0 1px $tag-border;

    &--grey {
      background: $gray-lightest;
      color: $gray-darker;
    }
  }

  &__desc {
    margin: 4px 0 0;
    font-size: $row-font-desc;
    color: $gray-darker;
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__path {
    margin-top: 6px;
    font-size: $row-font-meta;
    color: $gray-dark;

    code {
      font-family: $font-family-mono;
      font-size: $row-font-code;
    }
  }

  &__stock {
    color: $color-success-dark;
    font-weight: 600;

    &--no {
      color: $gray-dark;
      font-weight: 400;
    }
  }

  &__stockmeta {
    margin-left: 4px;
    color: $gray-dark;
  }

  &__rowprice {
    text-align: right;
  }

  &__price {
    font-size: $row-font-price;
    font-weight: 700;
    color: $text-color;
    font-variant-numeric: tabular-nums;
    line-height: 1.15;
  }

  &__was {
    font-size: $row-font-was;
    color: $gray-dark;
    text-decoration: line-through;
    font-variant-numeric: tabular-nums;
    margin-top: 2px;
  }

  &__sub {
    font-size: $row-font-meta;
    color: $gray-dark;
    margin-top: 3px;
    line-height: 1.35;
  }

  &__add {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1.5px solid var(--color-primary);
    background: $white;
    color: var(--color-primary);
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: all 0.14s ease;

    &:hover {
      background: var(--color-primary-light);
      color: $on-primary-light;
    }

    &.on {
      background: var(--color-primary);
      border-color: var(--color-primary);
      color: $white;

      svg {
        fill: $white;
      }
    }

    svg {
      @include icon;
      width: 15px;
      height: 15px;
    }
  }

  @include breakpoint-max(md) {
    grid-template-columns: minmax(0, 1fr) $col-action-sm;

    &__open {
      grid-template-columns: $col-thumb-sm minmax(0, 1fr);
      row-gap: 10px;
    }

    &__rowprice {
      grid-column: 1 / 3;
      text-align: left;
    }
  }
}
</style>
